import { motion } from 'framer-motion'
import { Button } from '@/components/ui/Button'

interface PhotoRetakeIntroProps {
  photoUrl: string | undefined
  onRetake: () => void
  onContinue: () => void
}

// Decorative sparkle component
const Sparkle = ({ className, size = 24, color = '#FF8C33', delay = 0 }: { className?: string; size?: number; color?: string; delay?: number }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    animate={{
      scale: [1, 1.3, 1],
      opacity: [0.7, 1, 0.7],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

export function PhotoRetakeIntro({ photoUrl, onRetake, onContinue }: PhotoRetakeIntroProps) {
  return (
    <div className="h-dvh flex flex-col overflow-hidden bg-gradient-to-b from-[#FFF5F0] via-[#FFFEF1] to-white relative">
      {/* Decorative sparkles */}
      <Sparkle className="absolute top-20 left-4 z-0" size={14} color="#FF8C33" delay={0} />
      <Sparkle className="absolute top-32 right-6 z-0" size={10} color="#F596FA" delay={0.5} />
      <Sparkle className="absolute bottom-40 left-8 z-0" size={12} color="#7182F8" delay={1} />
      <Sparkle className="absolute bottom-60 right-4 z-0" size={16} color="#46C0C6" delay={1.5} />

      {/* Floating circles in background */}
      <motion.div
        className="absolute top-24 right-8 w-24 h-24 rounded-full bg-[#46C0C6]/10 z-0"
        animate={{ y: [0, -10, 0], scale: [1, 1.05, 1] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-56 left-4 w-20 h-20 rounded-full bg-[#FF8C33]/10 z-0"
        animate={{ y: [0, 10, 0], scale: [1, 1.05, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1
            className="text-2xl font-bold text-[#075D61] mb-2"
            style={{ fontFamily: "'Anybody', sans-serif" }}
          >
            Your K-Beauty Recommendations
          </h1>
          <p className="text-gray-500">
            Based on AI analysis of your skin photo
          </p>
        </motion.div>

        {/* Photo preview card - matching PreviewScreen style */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, type: 'spring', damping: 20 }}
          className="relative mb-6"
        >
          {/* Photo card with shadow */}
          <div
            className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-white aspect-[3/4]"
            style={{ width: 'min(70vw, 240px)', maxHeight: '50vh' }}
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt="Your photo"
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <span className="text-gray-300 text-5xl">📷</span>
              </div>
            )}
          </div>

          {/* Success badge - matching PreviewScreen style */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: 'spring' }}
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#46C0C6] to-[#7182F8] text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2"
          >
            <span className="text-base">📸</span>
            Your Photo
          </motion.div>
        </motion.div>

        {/* Info card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100 max-w-sm w-full"
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl">✨</span>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Photo Quality Matters</h3>
              <p className="text-sm text-gray-600">
                For the most accurate K-beauty recommendations, make sure your photo has good lighting and shows your bare skin clearly.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Fixed footer with buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="shrink-0 bg-white/80 backdrop-blur-sm border-t border-gray-100 px-6 pt-4 pb-6 safe-bottom relative z-10"
      >
        <div className="max-w-sm mx-auto space-y-3">
          <Button
            size="lg"
            fullWidth
            onClick={onContinue}
            className="shadow-md"
          >
            Use This Photo
          </Button>
          <Button
            variant="outline"
            size="md"
            fullWidth
            onClick={onRetake}
          >
            Take a New Photo
          </Button>
        </div>
      </motion.div>
    </div>
  )
}
